import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { PRODUCTS } from "./products";
import { launchCheckout } from "./lib/upsell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INK = "#141414";
const CREAM = "#FAF7EF";
const LIME = "#D9F154";
const BLUE = "#3B5EDF";

const uid = () =>
  "sid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);

const GREETING =
  "¡Hola! Soy el copiloto de The Debt Guy. Te oriento desde cero y te llevo al recurso exacto que necesitas. Para empezar, cuéntame: ¿qué estás construyendo?";

const CHIPS = [
  { label: "Soy PyME", msg: "Tengo una PyME y busco capital de trabajo o crédito." },
  { label: "Plataforma de crédito", msg: "Soy una fintech/plataforma de crédito escalando un warehouse." },
  { label: "Corporativo / M&A", msg: "Me interesa corporate finance, M&A, bolsa o emisiones (FinCorp)." },
  { label: "Levanto equity", msg: "Soy fundador de startup levantando equity y mi primera deuda." },
];

// Parse assistant text → { text, actions[] }
function parse(content) {
  const actions = [];
  let text = content;
  text = text.replace(/\[\[BUY:([a-zA-Z0-9_]+)\]\]/g, (_, k) => {
    actions.push({ type: "buy", key: k });
    return "";
  });
  text = text.replace(/\[\[APPLY\]\]/g, () => {
    actions.push({ type: "apply" });
    return "";
  });
  text = text.replace(/\[\[LINK:([^\]]+)\]\]/g, (_, r) => {
    actions.push({ type: "link", route: r.trim() });
    return "";
  });
  return { text: text.replace(/\n{3,}/g, "\n\n").trim(), actions };
}

const ROUTE_LABELS = {
  "/#biblioteca": "Descargar un ebook gratis",
  "/toolkit": "Ver el Toolkit",
  "/programas": "Ver los programas",
  "/certificacion": "Ver la certificación",
  "/circle": "Ver The Debt Circle",
};

function ActionButtons({ actions }) {
  if (!actions || actions.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
      {actions.map((a, i) => {
        if (a.type === "buy") {
          const p = PRODUCTS[a.key];
          if (!p) return null;
          return (
            <button
              key={i}
              onClick={() => launchCheckout(a.key)}
              data-testid={`chat-buy-${a.key}`}
              style={{
                background: LIME, color: INK, border: `2px solid ${INK}`,
                borderRadius: 12, boxShadow: `3px 3px 0 ${INK}`, padding: "12px 16px",
                fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 13,
                cursor: "pointer", textAlign: "left", lineHeight: 1.25,
              }}
            >
              {`Comprar · ${p.name} · ${p.usd}${p.interval ? "/" + p.interval : ""} →`}
            </button>
          );
        }
        if (a.type === "apply") {
          return (
            <a key={i} href="/#pricing" data-testid="chat-apply"
              style={{
                background: BLUE, color: "#fff", border: `2px solid ${INK}`,
                borderRadius: 12, boxShadow: `3px 3px 0 ${INK}`, padding: "12px 16px",
                fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 13,
                textDecoration: "none", textAlign: "center",
              }}>
              Ver programas y precios →
            </a>
          );
        }
        return (
          <a key={i} href={a.route} data-testid={`chat-link`}
            style={{
              background: "transparent", color: INK, border: `2px solid ${INK}`,
              borderRadius: 12, padding: "11px 16px", fontFamily: "'Archivo', sans-serif",
              fontWeight: 800, fontSize: 13, textDecoration: "none", textAlign: "center",
            }}>
            {ROUTE_LABELS[a.route] || `Ver ${a.route}`} →
          </a>
        );
      })}
    </div>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sidRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    let sid = localStorage.getItem("tcg_chat_sid");
    if (!sid) {
      sid = uid();
      localStorage.setItem("tcg_chat_sid", sid);
    }
    sidRef.current = sid;
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open, busy]);

  // Keep the chat launcher above the sticky bottom CTA bar so they never overlap on mobile.
  const [fabBottom, setFabBottom] = useState(20);
  useEffect(() => {
    const update = () => {
      const bar = document.querySelector('[data-testid$="sticky-cta"]');
      if (bar) {
        const r = bar.getBoundingClientRect();
        const onScreen = r.height > 0 && r.top < window.innerHeight - 8;
        if (onScreen) {
          setFabBottom(Math.round(r.height) + 16);
          return;
        }
      }
      setFabBottom(20);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const id = setInterval(update, 400);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      clearInterval(id);
    };
  }, []);

  const send = async (text) => {
    const clean = (text || "").trim();
    if (!clean || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: clean }]);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sidRef.current,
        message: clean,
      });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMsgs((m) => [
        ...m,
        { role: "assistant", content: "Uf, se me trabó la conexión. ¿Me lo repites en un momento?" },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-launcher"
        aria-label="Abrir chat"
        style={{
          position: "fixed", right: 20, bottom: fabBottom, zIndex: 9998,
          background: LIME, color: INK, border: `2px solid ${INK}`,
          boxShadow: `4px 4px 0 ${INK}`, borderRadius: 999,
          padding: open ? "14px" : "14px 20px", display: "flex", alignItems: "center", gap: 10,
          cursor: "pointer", fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 14,
          transition: "bottom 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg>
        ) : (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24" fill={INK}><path d="M12 2C6.48 2 2 5.94 2 10.8c0 2.76 1.5 5.22 3.86 6.84L5 22l4.2-2.06c.9.2 1.84.32 2.8.32 5.52 0 10-3.94 10-8.8S17.52 2 12 2z" /></svg>
            Chatea con IA
          </>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          style={{
            position: "fixed", right: 20, bottom: fabBottom + 72, zIndex: 9998,
            width: "min(380px, calc(100vw - 40px))", height: "min(560px, calc(100vh - 130px))",
            background: CREAM, border: `2px solid ${INK}`, borderRadius: 20,
            boxShadow: `8px 8px 0 ${INK}`, display: "flex", flexDirection: "column", overflow: "hidden",
          }}
        >
          {/* Header */}
          <div style={{ background: INK, color: CREAM, padding: "16px 18px", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 999, background: LIME, display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${CREAM}` }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill={INK}><path d="M12 2C6.48 2 2 5.94 2 10.8c0 2.76 1.5 5.22 3.86 6.84L5 22l4.2-2.06c.9.2 1.84.32 2.8.32 5.52 0 10-3.94 10-8.8S17.52 2 12 2z" /></svg>
            </div>
            <div style={{ lineHeight: 1.15 }}>
              <div style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 14 }}>Copiloto · The Debt Guy</div>
              <div style={{ fontSize: 11, opacity: 0.7 }}>Te oriento y te llevo al paso correcto</div>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
            {msgs.map((m, i) => {
              if (m.role === "user") {
                return (
                  <div key={i} data-testid="chat-msg-user" style={{ alignSelf: "flex-end", maxWidth: "82%", background: BLUE, color: "#fff", borderRadius: "14px 14px 4px 14px", padding: "10px 13px", fontSize: 14, lineHeight: 1.4, fontFamily: "'Archivo', sans-serif" }}>
                    {m.content}
                  </div>
                );
              }
              const { text, actions } = parse(m.content);
              return (
                <div key={i} style={{ alignSelf: "flex-start", maxWidth: "90%" }}>
                  <div data-testid="chat-msg-assistant" style={{ background: "#fff", color: INK, border: `2px solid ${INK}`, borderRadius: "14px 14px 14px 4px", padding: "11px 14px", fontSize: 14, lineHeight: 1.45, fontFamily: "'Archivo', sans-serif", whiteSpace: "pre-wrap" }}>
                    {text}
                  </div>
                  <ActionButtons actions={actions} />
                </div>
              );
            })}

            {msgs.length === 1 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
                {CHIPS.map((c) => (
                  <button key={c.label} onClick={() => send(c.msg)} data-testid={`chat-chip-${c.label}`}
                    style={{ background: "transparent", border: `1.5px solid ${INK}`, borderRadius: 999, padding: "8px 13px", fontSize: 12.5, fontWeight: 700, fontFamily: "'Archivo', sans-serif", cursor: "pointer", color: INK }}>
                    {c.label}
                  </button>
                ))}
              </div>
            )}

            {busy && (
              <div data-testid="chat-typing" style={{ alignSelf: "flex-start", background: "#fff", border: `2px solid ${INK}`, borderRadius: "14px 14px 14px 4px", padding: "12px 16px", display: "flex", gap: 5 }}>
                {[0, 1, 2].map((d) => (
                  <span key={d} style={{ width: 7, height: 7, borderRadius: 999, background: INK, opacity: 0.5, animation: `tcgblink 1s ${d * 0.2}s infinite` }} />
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(input); }}
            style={{ display: "flex", gap: 8, padding: 12, borderTop: `2px solid ${INK}`, background: CREAM }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escribe tu pregunta…"
              data-testid="chat-input"
              style={{ flex: 1, border: `2px solid ${INK}`, borderRadius: 12, padding: "11px 13px", fontSize: 14, fontFamily: "'Archivo', sans-serif", outline: "none", background: "#fff" }}
            />
            <button type="submit" disabled={busy || !input.trim()} data-testid="chat-send"
              style={{ background: LIME, color: INK, border: `2px solid ${INK}`, borderRadius: 12, padding: "0 15px", cursor: "pointer", fontWeight: 800, fontFamily: "'Archivo', sans-serif" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={INK} strokeWidth="2.5"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" /></svg>
            </button>
          </form>
        </div>
      )}

      <style>{`@keyframes tcgblink { 0%,100%{opacity:.3;transform:translateY(0)} 50%{opacity:1;transform:translateY(-2px)} }`}</style>
    </>
  );
}
